// Logika widoków punktu kontrolnego (/checkpoint/:alias i /checkpoint/:alias/:markerId).
// Dane: odpowiedź /api/v1/splits/{alias} (runners, markers, splits, categories).
import { parseApiTime } from './polish-time.js'

export const START_TYPES = [1, 7]
export const FINISH_TYPES = [8, 93]

export function markerType (marker) {
  return Number(marker && marker.type)
}

// Pierwsza linia nazwy punktu (części oddzielone „|”), bez nazwy — „PK {sort}”.
export function markerLabel (marker) {
  if (!marker) return ''
  const first = String(marker.text || '').split('|').map(p => p.trim()).find(p => p.length > 0)
  if (first) return first
  if (START_TYPES.includes(markerType(marker))) return 'Start'
  if (FINISH_TYPES.includes(markerType(marker))) return 'Meta'
  return `PK ${marker.sort ?? marker.id}`
}

// Obsługa i media nie biorą miejsca (rola z sledz-api; starsze dane: kategoria 10 = obsługa).
export function isClassified (runner) {
  if (runner.role) return runner.role === 'runner'
  return Number(runner.category_id) !== 10
}

// runner_id → najwcześniejsze przejście przez punkt (ms). Pętle: liczy się pierwsze wejście.
export function passTimesForMarker (splits, markerId) {
  const passes = new Map()
  for (const split of splits || []) {
    if (String(split.marker_id) !== String(markerId)) continue
    const ms = parseApiTime(split.time)
    if (ms == null) continue
    const key = String(split.runner_id)
    const prev = passes.get(key)
    if (prev == null || ms < prev) passes.set(key, ms)
  }
  return passes
}

// Remisy dzielą miejsce: [10, 12, 12, 15] → [1, 2, 2, 4].
function assignPlaces (rows, field) {
  let place = 0
  let prevValue = null
  rows.forEach((row, index) => {
    if (row.fromStartMs !== prevValue) {
      place = index + 1
      prevValue = row.fromStartMs
    }
    row[field] = place
  })
}

// Wiersze dla jednego punktu: kto przeszedł, kiedy, czas od startu, miejsca (ogólne, kategoria, K/M), strata.
export function buildCheckpointRows ({ runners, passTimes, eventStartMs, categories }) {
  const categoryById = new Map((categories || []).map(c => [String(c.id), c]))
  const rows = []
  for (const runner of runners || []) {
    const passMs = passTimes.get(String(runner.id))
    if (passMs == null) continue
    const startMs = parseApiTime(runner.start_time) ?? eventStartMs ?? null
    const fromStartMs = startMs != null && passMs >= startMs ? passMs - startMs : null
    const category = categoryById.get(String(runner.category_id)) || null
    rows.push({
      id: runner.id,
      number: runner.number,
      name: runner.name,
      bio: runner.bio,
      gender: runner.gender === 'K' || runner.gender === 'M' ? runner.gender : null,
      categoryId: runner.category_id != null ? String(runner.category_id) : null,
      categoryName: category ? category.name : '',
      categoryColor: category && category.color ? category.color : null,
      dnf: !!Number(runner.dnf),
      classified: isClassified(runner) && !Number(runner.abs),
      passMs,
      fromStartMs,
      place: null,
      categoryPlace: null,
      genderPlace: null,
      gapMs: null
    })
  }

  const ranked = rows.filter(r => r.classified && r.fromStartMs != null)
    .sort((a, b) => a.fromStartMs - b.fromStartMs)
  assignPlaces(ranked, 'place')
  const best = ranked.length ? ranked[0].fromStartMs : null
  ranked.forEach(r => { r.gapMs = r.fromStartMs - best })

  const byGroup = (key) => {
    const groups = new Map()
    for (const row of ranked) {
      const value = row[key]
      if (value == null) continue
      if (!groups.has(value)) groups.set(value, [])
      groups.get(value).push(row)
    }
    return groups
  }
  for (const group of byGroup('categoryId').values()) assignPlaces(group, 'categoryPlace')
  for (const group of byGroup('gender').values()) assignPlaces(group, 'genderPlace')

  return rows
}

// Kolejność: 'latest' — najnowsze wejścia na górze, 'ranking' — wg czasu od startu (bez miejsca na końcu).
export function sortCheckpointRows (rows, order) {
  const copy = rows.slice()
  if (order === 'ranking') {
    return copy.sort((a, b) => {
      if (a.place != null && b.place != null) return a.place - b.place || a.passMs - b.passMs
      if (a.place != null) return -1
      if (b.place != null) return 1
      return a.passMs - b.passMs
    })
  }
  return copy.sort((a, b) => b.passMs - a.passMs)
}

// Pełna nazwa punktu: części z „|” połączone kropką ("PK 5 · Leśniczówka").
export function markerFullLabel (marker) {
  const parts = String((marker && marker.text) || '').split('|').map(p => p.trim()).filter(p => p.length > 0)
  return parts.length ? parts.join(' · ') : markerLabel(marker)
}

// Lista punktów z liczbą przejść i ostatnim wejściem (ekran wyboru punktu).
export function checkpointSummaries (markers, splits) {
  return (markers || []).slice()
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
    .map(marker => {
      const passes = passTimesForMarker(splits, marker.id)
      let lastPassMs = null
      for (const ms of passes.values()) {
        if (lastPassMs == null || ms > lastPassMs) lastPassMs = ms
      }
      return {
        id: marker.id,
        label: markerFullLabel(marker),
        sort: marker.sort,
        isStart: START_TYPES.includes(markerType(marker)),
        isFinish: FINISH_TYPES.includes(markerType(marker)),
        passCount: passes.size,
        lastPassMs
      }
    })
}
